import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import otpimg from "../../../Assets/otpimg.png";
import "./DoctorVerifyOTP.scss";
import {
  clearDoctorAuthError,
} from "../../../Features/Postes/Slice/doctorAuthSlice/doctorAuthSlice";
import {
  resendProviderEmailOtp,
  sendProviderEmailOtp,
  verifyDoctorOtp,
  verifyProviderEmailOtp,
} from "../../../Features/Postes/Thunk/doctorAuthThunk/doctorAuthThunk";
import { getDoctorProfile } from "../../../Features/Postes/Thunk/doctorExpertiseThunk";
import { tokenManager } from "../../../Api/tokenManager";
import { apiRequest } from "../../../Api/Api";
import { ENDPOINTS } from "../../../Api/Endpoints";

export default function DoctorVerifyOTP() {
  const isHospitalFlow = (() => {
    try {
      return String(window?.location?.pathname || "").toLowerCase().startsWith("/hospital");
    } catch (_) {
      return false;
    }
  })();
  const dashboardPath = isHospitalFlow ? "/hospital-dashboard" : "/doctor-dashboard";
  const profileCompletePath = isHospitalFlow ? "/hospital-profile-complete" : "/doctor-profile-complete";
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [emailOtp, setEmailOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [mobileResending, setMobileResending] = useState(false);
  const [emailResending, setEmailResending] = useState(false);
  const [mobileCooldown, setMobileCooldown] = useState(30);
  const [emailCooldown, setEmailCooldown] = useState(30);
  const [localAlert, setLocalAlert] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { verifyLoading, verifySuccess, error, pendingSignupMobile } = useSelector(
    (state) => state.doctorAuth
  );
  const { doctorProfile, profileLoading } = useSelector(
    (state) => state.doctorExpertise || {}
  );

  const rawMobile = location.state?.mobile || pendingSignupMobile || "";
  const email = String(location.state?.email || "").trim();

  const mobile = useMemo(() => {
    return String(rawMobile || "").replace(/\D/g, "").slice(0, 10);
  }, [rawMobile]);

  useEffect(() => {
    if (!mobileCooldown) return undefined;
    const id = window.setInterval(() => {
      setMobileCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [mobileCooldown]);

  useEffect(() => {
    if (!email || !emailCooldown) return undefined;
    const id = window.setInterval(() => {
      setEmailCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [email, emailCooldown]);

  useEffect(() => {
    if (!email || !mobile) return;
    dispatch(sendProviderEmailOtp({ mobile, email, isHospitalFlow }))
      .unwrap()
      .then(() => setLocalAlert("Email OTP sent"))
      .catch(() => {});
  }, [dispatch, email, mobile, isHospitalFlow]);

  useEffect(() => {
    if (!localAlert) return undefined;
    const id = window.setTimeout(() => setLocalAlert(""), 2200);
    return () => window.clearTimeout(id);
  }, [localAlert]);

  // After OTP success, fetch profile to decide redirect (approved → dashboard; verification page stays invisible)
  useEffect(() => {
    if (!verifySuccess) return;
    if (!tokenManager.getTokenForRole(isHospitalFlow ? "hospital" : "doctor")) return;
    dispatch(getDoctorProfile({ isHospitalFlow }));
  }, [verifySuccess, dispatch, isHospitalFlow]);

  // Normalize profile (API may return nested shape: { data: { ... } } or direct doctor object).
  const normalizedProfile = useMemo(() => {
    const p = doctorProfile || null;
    return p?.data?.data ?? p?.data ?? p?.doctor ?? p;
  }, [doctorProfile]);

  useEffect(() => {
    if (!verifySuccess || profileLoading || !normalizedProfile) return;
    const status = (normalizedProfile.status ?? "").toString().toLowerCase();
    if (status === "approved") {
      navigate(dashboardPath, { replace: true });
      return;
    }
    if (status === "pending_selection") {
      navigate(profileCompletePath, { replace: true });
      return;
    }
    if (status === "pending_admin" || status === "rejected") {
      navigate(profileCompletePath, { replace: true });
      return;
    }
    // fallback: e.g. unknown status
    navigate(profileCompletePath, { replace: true });
  }, [dashboardPath, profileCompletePath, verifySuccess, profileLoading, normalizedProfile, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearDoctorAuthError());
    };
  }, [dispatch]);

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!paste) return;

    const newOtp = paste.split("");
    while (newOtp.length < 4) newOtp.push("");
    setOtp(newOtp);

    setTimeout(() => {
      document.getElementById(`otp-${Math.min(paste.length, 4) - 1}`)?.focus();
    }, 0);
  };

  const handleVerify = async () => {
    const finalOtp = otp.join("").trim();

    if (!mobile) return;
    if (email && !emailVerified) {
      setLocalAlert("Please verify email OTP first.");
      return;
    }

    await dispatch(
      verifyDoctorOtp({
        mobile,
        otp: finalOtp,
        isHospitalFlow,
      })
    );
  };

  const handleVerifyEmailOtp = async () => {
    if (!email || !mobile) return;
    const finalOtp = String(emailOtp || "").replace(/\D/g, "").slice(0, 4);
    if (finalOtp.length !== 4) {
      setLocalAlert("Enter a valid 4-digit email OTP");
      return;
    }
    try {
      setEmailBusy(true);
      await dispatch(
        verifyProviderEmailOtp({
          mobile,
          email,
          otp: finalOtp,
          isHospitalFlow,
        })
      ).unwrap();
      setEmailVerified(true);
      setLocalAlert("Email verified successfully");
    } catch (e) {
      setLocalAlert(String(e || "Email OTP verification failed"));
    } finally {
      setEmailBusy(false);
    }
  };

  const handleResendMobileOtp = async () => {
    if (!mobile || mobileCooldown > 0) return;
    try {
      setMobileResending(true);
      const endpoint = isHospitalFlow ? ENDPOINTS.HOSPITAL_RESEND_OTP : ENDPOINTS.DOCTOR_RESEND_OTP;
      await apiRequest({
        method: "POST",
        url: endpoint,
        data: { mobile },
      });
      setMobileCooldown(30);
      setLocalAlert("Mobile OTP resent");
    } catch (e) {
      setLocalAlert(
        e?.response?.data?.message || e?.message || "Could not resend mobile OTP"
      );
    } finally {
      setMobileResending(false);
    }
  };

  const handleResendEmailOtp = async () => {
    if (!email || !mobile || emailCooldown > 0) return;
    try {
      setEmailResending(true);
      await dispatch(
        resendProviderEmailOtp({
          mobile,
          email,
          isHospitalFlow,
        })
      ).unwrap();
      setEmailCooldown(30);
      setEmailVerified(false);
      setLocalAlert("Email OTP resent");
    } catch (e) {
      setLocalAlert(String(e || "Could not resend email OTP"));
    } finally {
      setEmailResending(false);
    }
  };

  return (
    <div className="verify-page">
      {(error || localAlert) && <div className="top-alert">{error || localAlert}</div>}

      <div className="container">
        <div className="verify-card">
          <figure className="Otpimg">
            <img src={otpimg} alt="OTP" />
          </figure>

          <div className="enterr">
            <h2>Enter Your OTP</h2>
            <p>Enter the code sent to your mobile number.</p>
            {mobile ? <p>+91 {mobile}</p> : null}
            {email ? <p>{email}</p> : null}
          </div>

          {email ? (
            <div className="doctor-email-otp-wrap">
              <input
                type="text"
                value={emailOtp}
                maxLength={4}
                className="otp-input"
                placeholder="Email OTP"
                onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                disabled={emailBusy || emailVerified}
              />
              <button
                type="button"
                className="verify-btn"
                onClick={handleVerifyEmailOtp}
                disabled={emailBusy || emailVerified}
              >
                {emailVerified ? "Email verified" : emailBusy ? "Verifying..." : "Verify email OTP"}
              </button>
            </div>
          ) : null}

          <div className="otp-wrapper" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="otp-input"
                disabled={verifyLoading}
              />
            ))}
          </div>

          <button
            type="button"
            className="verify-btn"
            onClick={handleVerify}
            disabled={verifyLoading || (email && !emailVerified)}
          >
            {verifyLoading ? "Verifying..." : "Verify OTP"}
          </button>

          <div className="did">
            <p className="didP">Didn’t receive mobile OTP?</p>
            <p
              className="res"
              role="button"
              onClick={handleResendMobileOtp}
              style={{ cursor: mobileCooldown > 0 ? "not-allowed" : "pointer", opacity: mobileCooldown > 0 ? 0.7 : 1 }}
            >
              {mobileResending
                ? "Resending..."
                : mobileCooldown > 0
                  ? `Resend mobile OTP in ${mobileCooldown}s`
                  : "Resend mobile OTP"}
            </p>
            {email ? (
              <p
                className="res"
                role="button"
                onClick={handleResendEmailOtp}
                style={{ cursor: emailCooldown > 0 ? "not-allowed" : "pointer", opacity: emailCooldown > 0 ? 0.7 : 1 }}
              >
                {emailResending
                  ? "Resending email OTP..."
                  : emailCooldown > 0
                    ? `Resend email OTP in ${emailCooldown}s`
                    : "Resend email OTP"}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
